import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Icon, KpiTile, SectionLabel } from '@hive/ui';
import type { Screen } from '../api/client';
import { useEntitlements, useLiveScreens, useNow, useOrders, useOverview, useProgramme, useVenues } from '../api/hooks';
import { session } from '../auth/session';
import { Picker } from '../components/Picker';
import { PromoDialog } from '../components/PromoDialog';
import { ScreenCard } from '../components/ScreenCard';
import { TracksDialog } from '../components/TracksDialog';
import { VenueHeader } from '../components/VenueHeader';
import { EntitlementsCard, TonightCard } from '../components/VenuePanels';
import { venueTime } from '../lib/format';

export function DashboardPage() {
  const [params] = useSearchParams();
  const { screenId: pickerId } = useParams();
  const navigate = useNavigate();
  const { venues, error } = useVenues();
  const { user } = useAuth();
  const now = useNow();

  const venueId = params.get('venue') ?? venues?.find((v) => v.source === 'real')?.id ?? venues?.[0]?.id;
  const venue = venues?.find((v) => v.id === venueId);
  const { screens, offline, update, replaceAll } = useLiveScreens(venue?.id);
  const { data: overview } = useOverview(venue?.id);
  const { data: programme } = useProgramme(venue?.id);
  const { data: entitlements } = useEntitlements(venue?.id);
  const runsOrders = Boolean(user?.permissions.includes('orders.manage'));
  const { orders } = useOrders(venue?.id, runsOrders);
  const waiting = orders?.open.length ?? 0;
  const [promoOpen, setPromoOpen] = useState(false);
  const [tracksFor, setTracksFor] = useState<Screen | null>(null);

  useEffect(() => {
    if (venue) session.rememberVenue(venue.name);
  }, [venue]);

  // Legacy QR links: /?screen=<id> opens that screen's picker.
  const legacy = params.get('screen');
  useEffect(() => {
    if (legacy) navigate(`/screens/${legacy}/change`, { replace: true });
  }, [legacy, navigate]);

  // Installers set screens up; they do not change what plays.
  if (user && !user.permissions.includes('screens.control')) return <Navigate to={`/setup${venueId ? `?venue=${venueId}` : ''}`} replace />;

  if (error) {
    return (
      <main className="hv-root k-page">
        <p className="k-inline-error">Could not load your venues. Refresh the page to try again.</p>
      </main>
    );
  }

  if (venues && venues.length === 0) {
    return (
      <main className="hv-root k-page">
        <p className="k-inline-warn">You are not assigned to a venue yet. Ask your Hive admin to add you to one, then sign in again.</p>
        <button type="button" className="hv-btn hv-btn--secondary hv-btn--sm k-signout-solo" onClick={() => session.clear()}>
          Sign out
        </button>
      </main>
    );
  }

  const picking = pickerId ? screens?.find((s) => s.id === pickerId) : undefined;
  const updateMany = (list: Screen[]) => (list.length === 1 ? update(list[0]) : replaceAll(list));
  const location = [venue?.area, venue?.city].filter(Boolean).join(', ');
  const context = [location, overview?.localTime, overview?.sessionLabel].filter(Boolean).join(' · ');
  const sponsor = programme?.loops.find((l) => l.kind === 'loop' && l.detail);

  return (
    <main className="hv-root k-page">
      {venue && (
        <VenueHeader
          name={venue.name}
          context={context}
          extra={
            venues && venues.length > 1 ? (
              <label className="k-venue-switch">
                <span className="hv-sr-only">Venue</span>
                <select value={venue.id} onChange={(e) => navigate(`/?venue=${encodeURIComponent(e.target.value)}`)}>
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                      {v.source === 'simulated' ? ' (simulated)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            ) : undefined
          }
        />
      )}
      {offline && <p className="k-inline-warn">Connection lost. Showing the last known state.</p>}

      <section aria-label="Tonight at a glance" className="k-kpis">
        <KpiTile
          icon={<Icon name="screen" />}
          value={overview ? `${overview.screensOnline} of ${overview.screensTotal}` : '…'}
          label="Screens online"
          tone={overview && overview.screensOnline < overview.screensTotal ? 'red' : 'blue'}
        />
        <KpiTile
          icon={<Icon name="play" />}
          tone="teal"
          value={overview ? `${overview.liveNow} live` : '…'}
          label={overview ? `${overview.upcomingTonight} more fixture${overview.upcomingTonight === 1 ? '' : 's'} tonight` : 'Fixtures tonight'}
        />
        <KpiTile icon={<Icon name="qr" />} value={overview?.qrScans.count ?? '…'} label={`QR scans since ${overview ? venueTime(overview.qrScans.since, venue?.timezone) : ''}`} />
        {runsOrders && venue ? (
          <Link to={`/orders?venue=${venue.id}`} className="k-kpi-link" aria-label={`Nectar orders: ${waiting} waiting. Open the order queue`}>
            {/* Live orders only (new or being prepared); served and cancelled ones are on the Orders page. */}
            <KpiTile
              icon={<Icon name="glass" />}
              tone={waiting > 0 ? 'teal' : 'blue'}
              value={orders ? `${waiting} waiting` : '…'}
              label="Nectar orders to serve · open the queue"
            />
          </Link>
        ) : (
          <KpiTile icon={<Icon name="glass" />} value={overview ? `${overview.orders.count} order${overview.orders.count === 1 ? '' : 's'}` : '…'} label="Nectar table orders" />
        )}
      </section>

      <section aria-label="Screens" className="k-screens">
        <div className="k-screens__head">
          <SectionLabel>Screens</SectionLabel>
          <span className="hv-muted k-small">Live thumbnails refresh every 10 s</span>
        </div>
        {!screens ? (
          <p className="hv-muted">Loading screens…</p>
        ) : screens.length === 0 ? (
          <p className="hv-muted">This venue has no screens yet.</p>
        ) : (
          <div className="k-screen-grid">
            {screens.map((s) => (
              <ScreenCard
                key={s.id}
                screen={s}
                now={now}
                onChange={(screen) => navigate(`/screens/${screen.id}/change${venue ? `?venue=${venue.id}` : ''}`)}
                onPair={(screen) => navigate(`/pair?venue=${screen.venueId}&screen=${screen.id}`)}
                onTracks={setTracksFor}
              />
            ))}
          </div>
        )}
      </section>

      <section className="k-bottom">
        <EntitlementsCard entitlements={entitlements} />
        <TonightCard programme={programme} screens={screens ?? []} timezone={venue?.timezone} onPromo={() => setPromoOpen(true)} />
      </section>

      {picking && screens && (
        <Picker
          screen={picking}
          screens={screens}
          programme={programme}
          timezone={venue?.timezone}
          onUpdated={updateMany}
          onClose={() => navigate(venue ? `/?venue=${venue.id}` : '/')}
        />
      )}
      {tracksFor && <TracksDialog screen={tracksFor} onClose={() => setTracksFor(null)} onSaved={(s) => update(s)} />}
      {promoOpen && screens && (
        <PromoDialog
          screens={screens}
          defaultOffer={sponsor ? { sponsor: sponsor.title, title: sponsor.detail, body: 'Order to your table before the second half' } : undefined}
          patronUrl={venue?.slug ? `${window.location.origin}/p/${venue.slug}` : undefined}
          onUpdated={update}
          onClose={() => setPromoOpen(false)}
        />
      )}
    </main>
  );
}
